import { useEffect, useRef, useState } from "react";

type Options = {
    startSearch?: string;
    defaultDelay?: number;
    actionOnDelay?: (validatedSearch: string) => void;
};

export interface DelayedSearch {
    search: string;
    delayedSearch: string;
    setSearch: (newSearch: string) => void;
    flushSearch: () => void;
    resetSearch: () => void;
    doesSearchMatch: (valueToMatchWithSearch: string) => boolean;
}

export const useDelayedSearch = (options?: Options): DelayedSearch => {
    const { startSearch = "", defaultDelay = 1000, actionOnDelay } = options ?? {};
    const searchTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (searchTimeoutId.current !== null) {
                clearTimeout(searchTimeoutId.current);
            }
        };
    }, []);

    const [search, _setSearch] = useState(startSearch);
    const [delayedSearch, setDelayedSearch] = useState(startSearch);

    const doesSearchMatch = (searchValue: string) => {
        if (!search) return true;
        return searchValue.toLowerCase().includes(search.toLowerCase());
    };

    const setSearchHandler = (newSearch: string) => {
        // Immediate feedback for the search bar.
        _setSearch(newSearch);

        const existingTimeout = searchTimeoutId.current;
        if (existingTimeout !== null) {
            clearTimeout(existingTimeout);
        }

        // But the fetch is delayed.
        const newTimeoutId = setTimeout(() => {
            setDelayedSearch(newSearch);
            actionOnDelay?.(newSearch);
        }, defaultDelay);
        searchTimeoutId.current = newTimeoutId;
    };

    // Immediately apply the current search value, cancelling any pending debounce.
    const flushSearch = () => {
        const existingTimeout = searchTimeoutId.current;
        if (existingTimeout !== null) {
            clearTimeout(existingTimeout);
            searchTimeoutId.current = null;
        }
        setDelayedSearch(search);
        actionOnDelay?.(search);
    };

    const resetSearch = () => {
        _setSearch("");
        setDelayedSearch("");
    };

    return {
        search: search,
        delayedSearch: delayedSearch,
        setSearch: setSearchHandler,
        flushSearch,
        resetSearch,
        doesSearchMatch,
    };
};
